import { useState } from 'react';
import { MenuIcon } from 'lucide-react';
import { useSite } from '../content';
import { useScrolled } from '../hooks/useScrolled';
import { MobileMenu } from './MobileMenu';

interface SiteHeaderProps {
  onOpenContact: (inquiryType?: string) => void;
}

export function SiteHeader({ onOpenContact }: SiteHeaderProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const scrolled = useScrolled(80);
  const site = useSite();
  const { nav } = site.site;
  const { hero } = site;

  const shellClass = [
    'fixed top-0 left-0 w-full z-50 transition-all duration-300',
    scrolled
      ? 'bg-slate-dark/90 backdrop-blur-md border-b border-textlight/10 py-3'
      : 'bg-transparent py-6 md:py-8',
  ].join(' ');

  const logoClass = [
    'transition-opacity duration-300',
    scrolled ? 'opacity-100' : 'opacity-0 pointer-events-none',
  ].join(' ');

  return (
    <>
      <header role="banner" className={shellClass}>
        <div
          className={`flex items-center px-4 md:px-12 lg:px-16 ${
            scrolled ? 'justify-between' : 'justify-center'
          }`}
        >
          {scrolled && (
            <a
              href="#main-content"
              aria-label={`${site.site.title} home`}
              className={`${logoClass} inline-block origin-left`}
            >
              <img
                src={hero.logoImageUrl}
                alt={hero.logoAlt}
                className="h-7 md:h-[3.25rem] w-auto transition-transform duration-300 ease-out hover:scale-[1.15] focus-visible:scale-[1.15]"
              />
            </a>
          )}

          <nav aria-label="Primary" className="flex items-center">
            <ul className="hidden md:flex space-x-6 lg:space-x-10 text-xs lg:text-sm font-sans tracking-[0.2em] uppercase text-textlight/90">
              {nav.map((item) => (
                <li key={item.label}>
                  {item.action === 'contact' ? (
                    <button
                      type="button"
                      onClick={() => onOpenContact()}
                      className="![display:inline] p-0 m-0 bg-transparent border-0 appearance-none font-[inherit] text-[inherit] tracking-[inherit] uppercase leading-[inherit] align-baseline hover:text-white transition-colors duration-300"
                    >
                      {item.label}
                    </button>
                  ) : (
                    <a
                      href={item.sectionId ? `#${item.sectionId}` : '#'}
                      className="hover:text-white transition-colors duration-300"
                    >
                      {item.label}
                    </a>
                  )}
                </li>
              ))}
            </ul>

            <button
              type="button"
              onClick={() => setMenuOpen(true)}
              aria-label="Open menu"
              aria-expanded={menuOpen}
              aria-controls="mobile-menu"
              className="md:hidden text-textlight/90 hover:text-white transition-colors p-2 -mr-2 min-h-[44px] min-w-[44px] flex items-center justify-center"
            >
              <MenuIcon className="w-6 h-6" aria-hidden="true" />
            </button>
          </nav>
        </div>
      </header>

      <div id="mobile-menu">
        <MobileMenu
          isOpen={menuOpen}
          onClose={() => setMenuOpen(false)}
          items={nav}
          onOpenContact={onOpenContact}
        />
      </div>
    </>
  );
}
